/**
 * Application Detail Page
 * Full view of a single application with timeline, documents, and actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  Avatar,
  Skeleton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Flight as FlightIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Public as PublicIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  NotificationsActive as ReminderIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';

// Types
interface Application {
  id: string;
  tracking_number: string;
  reference_number: string;
  user_id: string;
  destination_country: string;
  destination_country_code: string;
  visa_type: string;
  visa_subtype: string;
  status: string;
  priority: string;
  
  // Applicant info
  applicant_first_name: string;
  applicant_last_name: string;
  applicant_middle_name: string;
  applicant_email: string;
  applicant_phone: string;
  applicant_date_of_birth: string;
  applicant_nationality: string;
  applicant_gender: string;
  
  // Passport info
  passport_number: string;
  passport_issue_date: string;
  passport_expiry_date: string;
  passport_country: string;
  
  // Travel info
  travel_date: string;
  return_date: string;
  purpose_of_travel: string;
  accommodation_address: string;
  
  // Fees
  government_fee: number;
  service_fee: number;
  processing_fee: number;
  total_fee: number;
  currency: string;
  
  // Dates
  created_at: string;
  updated_at: string;
  submitted_at: string;
  processed_at: string;
  
  // Portal tracking
  portal_name: string;
  portal_submitted_at: string;
  submission_reference: string;
  confirmation_number: string;
  last_portal_status: string;
  portal_status_checked_at: string;
  decision_date: string;
  decision_details: string;
  visa_number: string;
  visa_valid_from: string;
  visa_valid_until: string;
  
  // Extra
  notes: string;
  admin_notes: string;
  form_data: any;
}

interface StatusHistoryItem {
  id: string;
  status: string;
  portal_status: string;
  source: string;
  notes: string;
  created_at: string;
}

interface ReminderItem {
  id: string;
  type: string;
  title: string;
  message: string;
  due_date: string;
  status: string;
}

// Status configuration
const STATUS_CONFIG: Record<string, { color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; step: number; label: string }> = {
  draft: { color: 'default', step: 0, label: 'Draft' },
  submitted: { color: 'info', step: 1, label: 'Submitted' },
  pending: { color: 'warning', step: 1, label: 'Pending Review' },
  under_review: { color: 'primary', step: 2, label: 'Under Review' },
  processing: { color: 'primary', step: 2, label: 'Processing' },
  approved: { color: 'success', step: 3, label: 'Approved' },
  rejected: { color: 'error', step: 3, label: 'Rejected' },
  cancelled: { color: 'default', step: -1, label: 'Cancelled' },
};

// Timeline steps
const TIMELINE_STEPS = [
  { label: 'Application Created', description: 'Your application has been saved' },
  { label: 'Submitted for Review', description: 'Application submitted and awaiting review' },
  { label: 'Processing', description: 'Your application is being processed' },
  { label: 'Decision Made', description: 'Final decision on your application' },
];

// Flag URL helper
const getFlagUrl = (countryCode: string) => 
  `https://flagcdn.com/w80/${countryCode?.toLowerCase()}.png`;

export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // State
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packageError, setPackageError] = useState<string | null>(null);
  const [downloadingPackage, setDownloadingPackage] = useState(false);
  const [downloadingPortalPackage, setDownloadingPortalPackage] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Portal tracking state
  const [portalDialogOpen, setPortalDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [portalSubmitting, setPortalSubmitting] = useState(false);
  const [portalForm, setPortalForm] = useState({ portalName: '', submissionReference: '', confirmationNumber: '', notes: '' });
  const [statusForm, setStatusForm] = useState({ portalStatus: '', confirmationNumber: '', decisionDetails: '', visaNumber: '', visaValidFrom: '', visaValidUntil: '', notes: '' });
  const [timeline, setTimeline] = useState<StatusHistoryItem[]>([]);
  const [reminders, setReminders] = useState<ReminderItem[]>([]);
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [remindersExpanded, setRemindersExpanded] = useState(false);

  // Fetch application
  const fetchApplication = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      
      const res = await fetch(`${API_BASE_URL}/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Application not found');
      }

      const json = await res.json();
      setApplication(json.data || json);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch timeline
  const fetchTimeline = useCallback(async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const res = await fetch(`${API_BASE_URL}/applications/${id}/timeline`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setTimeline(json.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch timeline:', err);
    }
  }, [id]);

  // Fetch reminders
  const fetchReminders = useCallback(async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const res = await fetch(`${API_BASE_URL}/applications/${id}/reminders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setReminders(json.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch reminders:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  useEffect(() => {
    if (application) {
      fetchTimeline();
      fetchReminders();
    }
  }, [application, fetchTimeline, fetchReminders]);

  // Handlers
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      await fetch(`${API_BASE_URL}/applications/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate('/applications');
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleCopyTracking = () => {
    if (application?.tracking_number) {
      navigator.clipboard.writeText(application.tracking_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadPackage = async () => {
    if (!id) return;
    try {
      setPackageError(null);
      setDownloadingPackage(true);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const response = await fetch(`${API_BASE_URL}/applications/${id}/submission-package?format=zip`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        throw new Error(errJson?.message || 'Failed to download submission package');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename="([^"]+)"/i);
      const filename = filenameMatch?.[1] || `tripnovac-submission-package-${id}.zip`;
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setPackageError(err?.message || 'Failed to download submission package');
    } finally {
      setDownloadingPackage(false);
    }
  };

  const handleDownloadPortalPackage = async () => {
    if (!id) return;
    try {
      setPackageError(null);
      setDownloadingPortalPackage(true);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const response = await fetch(`${API_BASE_URL}/applications/${id}/portal-package`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        throw new Error(errJson?.message || 'Failed to download portal package');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename="([^"]+)"/i);
      const filename = filenameMatch?.[1] || `visa-application-${id}.doc`;
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      setPackageError(err?.message || 'Failed to download portal package');
    } finally {
      setDownloadingPortalPackage(false);
    }
  };

  const handleRecordPortalSubmission = async () => {
    if (!id || !portalForm.portalName) return;
    try {
      setPortalSubmitting(true);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const response = await fetch(`${API_BASE_URL}/applications/${id}/portal-submission`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(portalForm),
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        throw new Error(errJson?.message || 'Failed to record portal submission');
      }
      const json = await response.json();
      setApplication(json.data || application);
      setPortalDialogOpen(false);
      setPortalForm({ portalName: '', submissionReference: '', confirmationNumber: '', notes: '' });
      fetchTimeline();
      fetchReminders();
    } catch (err: any) {
      setPackageError(err?.message || 'Failed to record portal submission');
    } finally {
      setPortalSubmitting(false);
    }
  };

  const handleUpdatePortalStatus = async () => {
    if (!id || !statusForm.portalStatus) return;
    try {
      setPortalSubmitting(true);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const response = await fetch(`${API_BASE_URL}/applications/${id}/portal-status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(statusForm),
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => null);
        throw new Error(errJson?.message || 'Failed to update portal status');
      }
      const json = await response.json();
      setApplication(json.data || application);
      setStatusDialogOpen(false);
      setStatusForm({ portalStatus: '', confirmationNumber: '', decisionDetails: '', visaNumber: '', visaValidFrom: '', visaValidUntil: '', notes: '' });
      fetchTimeline();
      fetchReminders();
    } catch (err: any) {
      setPackageError(err?.message || 'Failed to update portal status');
    } finally {
      setPortalSubmitting(false);
    }
  };

  const handleDismissReminder = async (reminderId: string) => {
    if (!id) return;
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      await fetch(`${API_BASE_URL}/applications/${id}/reminders/${reminderId}/dismiss`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchReminders();
    } catch (err) {
      console.error('Failed to dismiss reminder:', err);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: application?.currency || 'USD',
    }).format(amount || 0);
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4 }}>
        <Container maxWidth="lg">
          <Skeleton variant="rectangular" height={60} sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rectangular" height={400} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error || !application) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Application not found'}
          </Alert>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/applications')}>
            Back to Applications
          </Button>
        </Container>
      </Box>
    );
  }

  const statusConfig = STATUS_CONFIG[application.status] || STATUS_CONFIG.pending;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/applications')}
            sx={{ mb: 2 }}
          >
            Back to Applications
          </Button>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar
                src={getFlagUrl(application.destination_country_code)}
                sx={{ width: 64, height: 64, boxShadow: 2 }}
                variant="rounded"
              >
                🌍
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {application.destination_country} - {application.visa_type}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    label={statusConfig.label}
                    color={statusConfig.color}
                    sx={{ fontWeight: 600 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Tracking: 
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="primary">
                      {application.tracking_number}
                    </Typography>
                    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                      <IconButton size="small" onClick={handleCopyTracking}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchApplication}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton onClick={() => window.print()}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton onClick={handleDownloadPackage} disabled={downloadingPackage}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              {application.status === 'draft' && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/applications/${id}/edit`)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {packageError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {packageError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {/* Application Timeline */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Application Timeline
              </Typography>
              <Stepper 
                activeStep={statusConfig.step} 
                orientation="vertical"
                sx={{ mt: 2 }}
              >
                {TIMELINE_STEPS.map((step, index) => (
                  <Step key={step.label} completed={index < statusConfig.step}>
                    <StepLabel
                      optional={
                        index === statusConfig.step && (
                          <Typography variant="caption" color="text.secondary">
                            Current Status
                          </Typography>
                        )
                      }
                    >
                      {step.label}
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                      {index === 0 && application.created_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {formatDate(application.created_at)}
                        </Typography>
                      )}
                      {index === 1 && application.submitted_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {formatDate(application.submitted_at)}
                        </Typography>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Paper>

            {/* Portal Submission Tracking */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PublicIcon color="primary" />
                  <Typography variant="h6" fontWeight="600">
                    Government Portal Tracking
                  </Typography>
                </Box>
                {!application.portal_name ? (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AssignmentIcon />}
                    onClick={() => setPortalDialogOpen(true)}
                  >
                    Record Portal Submission
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => setStatusDialogOpen(true)}
                  >
                    Update Status
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              {application.portal_name ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Portal</Typography>
                    <Typography variant="body1" fontWeight="500">{application.portal_name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Submitted On</Typography>
                    <Typography variant="body1" fontWeight="500">{formatDate(application.portal_submitted_at)}</Typography>
                  </Grid>
                  {application.submission_reference && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Submission Reference</Typography>
                      <Typography variant="body1" fontWeight="500">{application.submission_reference}</Typography>
                    </Grid>
                  )}
                  {application.confirmation_number && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Confirmation Number</Typography>
                      <Typography variant="body1" fontWeight="500">{application.confirmation_number}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Current Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {application.last_portal_status === 'approved' && <CheckCircleIcon color="success" fontSize="small" />}
                      {application.last_portal_status === 'rejected' && <CancelIcon color="error" fontSize="small" />}
                      {!['approved', 'rejected'].includes(application.last_portal_status || '') && <ScheduleIcon color="warning" fontSize="small" />}
                      <Typography variant="body1" fontWeight="500" sx={{ textTransform: 'capitalize' }}>
                        {application.last_portal_status || 'Pending'}
                      </Typography>
                    </Box>
                  </Grid>
                  {application.portal_status_checked_at && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Last Checked</Typography>
                      <Typography variant="body1" fontWeight="500">{formatDate(application.portal_status_checked_at)}</Typography>
                    </Grid>
                  )}
                  {application.decision_date && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Decision Date</Typography>
                      <Typography variant="body1" fontWeight="500">{formatDate(application.decision_date)}</Typography>
                    </Grid>
                  )}
                  {application.decision_details && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Decision Details</Typography>
                      <Typography variant="body2">{application.decision_details}</Typography>
                    </Grid>
                  )}
                  {application.visa_number && (
                    <>
                      <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Visa Number</Typography>
                        <Typography variant="body1" fontWeight="600" color="success.main">{application.visa_number}</Typography>
                      </Grid>
                      {application.visa_valid_from && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Valid From</Typography>
                          <Typography variant="body1" fontWeight="500">{formatDate(application.visa_valid_from)}</Typography>
                        </Grid>
                      )}
                      {application.visa_valid_until && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Valid Until</Typography>
                          <Typography variant="body1" fontWeight="500">{formatDate(application.visa_valid_until)}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    After completing your application, download the portal-ready package below and submit it to the official government visa portal.
                    Then record your submission here to track status and receive reminders.
                  </Typography>
                </Alert>
              )}

              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={downloadingPortalPackage ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
                  onClick={handleDownloadPortalPackage}
                  disabled={downloadingPortalPackage}
                >
                  Download Portal-Ready Package (.doc)
                </Button>
                <Button
                  variant="outlined"
                  startIcon={downloadingPackage ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                  onClick={handleDownloadPackage}
                  disabled={downloadingPackage}
                >
                  Download Technical Archive (.zip)
                </Button>
              </Box>
            </Paper>

            {/* Status History Timeline */}
            {timeline.length > 0 && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setTimelineExpanded(!timelineExpanded)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon color="action" />
                    <Typography variant="h6" fontWeight="600">Status History</Typography>
                    <Chip label={timeline.length} size="small" />
                  </Box>
                  {timelineExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={timelineExpanded}>
                  <List sx={{ mt: 2 }}>
                    {timeline.map((item, idx) => (
                      <ListItem key={item.id} sx={{ px: 0, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                          {item.status === 'approved' ? <CheckCircleIcon color="success" /> :
                           item.status === 'rejected' ? <CancelIcon color="error" /> :
                           <ScheduleIcon color="action" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" fontWeight="500" sx={{ textTransform: 'capitalize' }}>
                                {item.portal_status || item.status}
                              </Typography>
                              <Chip label={item.source} size="small" variant="outlined" />
                            </Box>
                          }
                          secondary={
                            <>
                              {item.notes && <Typography variant="body2" color="text.secondary">{item.notes}</Typography>}
                              <Typography variant="caption" color="text.secondary">{formatDate(item.created_at)}</Typography>
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Paper>
            )}

            {/* Reminders */}
            {reminders.length > 0 && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setRemindersExpanded(!remindersExpanded)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReminderIcon color="warning" />
                    <Typography variant="h6" fontWeight="600">Reminders</Typography>
                    <Chip label={reminders.filter(r => r.status === 'pending').length} size="small" color="warning" />
                  </Box>
                  {remindersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={remindersExpanded}>
                  <List sx={{ mt: 2 }}>
                    {reminders.map((reminder) => (
                      <ListItem
                        key={reminder.id}
                        sx={{ px: 0, opacity: reminder.status === 'dismissed' ? 0.5 : 1 }}
                        secondaryAction={
                          reminder.status === 'pending' && (
                            <Button size="small" onClick={() => handleDismissReminder(reminder.id)}>Dismiss</Button>
                          )
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <ScheduleIcon color={reminder.status === 'pending' ? 'warning' : 'disabled'} />
                        </ListItemIcon>
                        <ListItemText
                          primary={reminder.title}
                          secondary={
                            <>
                              {reminder.message && <Typography variant="body2" color="text.secondary">{reminder.message}</Typography>}
                              <Typography variant="caption" color="text.secondary">Due: {formatDate(reminder.due_date)}</Typography>
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Paper>
            )}

            {/* Applicant Information */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Applicant Information
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <PersonIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Full Name</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {application.applicant_first_name} {application.applicant_middle_name} {application.applicant_last_name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CalendarIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatDate(application.applicant_date_of_birth)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <EmailIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {application.applicant_email || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <PhoneIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {application.applicant_phone || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <LocationIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Nationality</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {application.applicant_nationality || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Passport Information */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Passport Information
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Passport Number</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {application.passport_number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Country of Issue</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {application.passport_country || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Issue Date</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatDate(application.passport_issue_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Expiry Date</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatDate(application.passport_expiry_date)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Travel Information */}
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Travel Information
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FlightIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Travel Date</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatDate(application.travel_date)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FlightIcon color="action" sx={{ transform: 'rotate(180deg)' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Return Date</Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatDate(application.return_date)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Purpose of Travel</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {application.purpose_of_travel || '-'}
                  </Typography>
                </Grid>
                {application.accommodation_address && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Accommodation Address</Typography>
                    <Typography variant="body1" fontWeight="500">
                      {application.accommodation_address}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Fee Summary */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Fee Summary
              </Typography>
              <Divider sx={{ my: 2 }} />
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText primary="Government Fee" />
                  <Typography variant="body2" fontWeight="500">
                    {formatCurrency(application.government_fee)}
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText primary="Service Fee" />
                  <Typography variant="body2" fontWeight="500">
                    {formatCurrency(application.service_fee)}
                  </Typography>
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText primary="Processing Fee" />
                  <Typography variant="body2" fontWeight="500">
                    {formatCurrency(application.processing_fee)}
                  </Typography>
                </ListItem>
                <Divider sx={{ my: 1 }} />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary={<Typography fontWeight="600">Total</Typography>} 
                  />
                  <Typography variant="h6" fontWeight="700" color="primary">
                    {formatCurrency(application.total_fee)}
                  </Typography>
                </ListItem>
              </List>
              
              {application.status === 'draft' && (
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{ mt: 2 }}
                  onClick={() => navigate(`/payment`)}
                >
                  Proceed to Payment
                </Button>
              )}
            </Paper>

            {/* Quick Info */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Quick Info
              </Typography>
              <Divider sx={{ my: 2 }} />
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary="Application ID" 
                    secondary={application.id?.slice(0, 8) + '...'}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary="Reference Number" 
                    secondary={application.reference_number || '-'}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary="Priority" 
                    secondary={application.priority || 'Standard'}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary="Created" 
                    secondary={formatDate(application.created_at)}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary="Last Updated" 
                    secondary={formatDate(application.updated_at)}
                  />
                </ListItem>
              </List>
            </Paper>

            {/* Notes */}
            {application.notes && (
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Notes
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {application.notes}
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Application?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this application? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Record Portal Submission Dialog */}
        <Dialog open={portalDialogOpen} onClose={() => setPortalDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Record Government Portal Submission</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              After submitting your application to the official government portal, record the details here to track your visa status.
            </Typography>
            <TextField
              select
              fullWidth
              label="Government Portal"
              value={portalForm.portalName}
              onChange={(e) => setPortalForm({ ...portalForm, portalName: e.target.value })}
              sx={{ mb: 2 }}
              required
            >
              <MenuItem value="">Select Portal</MenuItem>
              <MenuItem value="UK Gov Visa Portal">UK Gov Visa Portal</MenuItem>
              <MenuItem value="US CEAC Portal">US CEAC Portal</MenuItem>
              <MenuItem value="Schengen VIS">Schengen VIS</MenuItem>
              <MenuItem value="Australia ImmiAccount">Australia ImmiAccount</MenuItem>
              <MenuItem value="Canada IRCC Portal">Canada IRCC Portal</MenuItem>
              <MenuItem value="Dubai ICA Portal">Dubai ICA Portal</MenuItem>
              <MenuItem value="South Africa VFS">South Africa VFS</MenuItem>
              <MenuItem value="India VFS">India VFS</MenuItem>
              <MenuItem value="China CVASC">China CVASC</MenuItem>
              <MenuItem value="Japan eVisa">Japan eVisa</MenuItem>
              <MenuItem value="Other">Other Portal</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Submission Reference (if provided)"
              value={portalForm.submissionReference}
              onChange={(e) => setPortalForm({ ...portalForm, submissionReference: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="e.g., GWF123456789"
            />
            <TextField
              fullWidth
              label="Confirmation Number (if provided)"
              value={portalForm.confirmationNumber}
              onChange={(e) => setPortalForm({ ...portalForm, confirmationNumber: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="e.g., AA12345678"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (optional)"
              value={portalForm.notes}
              onChange={(e) => setPortalForm({ ...portalForm, notes: e.target.value })}
              placeholder="Any additional notes about your submission"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPortalDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRecordPortalSubmission}
              variant="contained"
              disabled={!portalForm.portalName || portalSubmitting}
              startIcon={portalSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
            >
              Record Submission
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Portal Status Dialog */}
        <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Portal Status</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              After checking your application status on the government portal, update the status here.
            </Typography>
            <TextField
              select
              fullWidth
              label="Current Status"
              value={statusForm.portalStatus}
              onChange={(e) => setStatusForm({ ...statusForm, portalStatus: e.target.value })}
              sx={{ mb: 2 }}
              required
            >
              <MenuItem value="">Select Status</MenuItem>
              <MenuItem value="pending">Pending / Awaiting Review</MenuItem>
              <MenuItem value="in_review">In Review / Processing</MenuItem>
              <MenuItem value="additional_documents_required">Additional Documents Required</MenuItem>
              <MenuItem value="interview_scheduled">Interview Scheduled</MenuItem>
              <MenuItem value="approved">Approved / Granted</MenuItem>
              <MenuItem value="rejected">Rejected / Denied</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Confirmation/Reference Number"
              value={statusForm.confirmationNumber}
              onChange={(e) => setStatusForm({ ...statusForm, confirmationNumber: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="Update if changed"
            />
            {statusForm.portalStatus === 'approved' && (
              <>
                <TextField
                  fullWidth
                  label="Visa Number"
                  value={statusForm.visaNumber}
                  onChange={(e) => setStatusForm({ ...statusForm, visaNumber: e.target.value })}
                  sx={{ mb: 2 }}
                  placeholder="e.g., V12345678"
                />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Valid From"
                      value={statusForm.visaValidFrom}
                      onChange={(e) => setStatusForm({ ...statusForm, visaValidFrom: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Valid Until"
                      value={statusForm.visaValidUntil}
                      onChange={(e) => setStatusForm({ ...statusForm, visaValidUntil: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Decision Details / Notes"
              value={statusForm.decisionDetails}
              onChange={(e) => setStatusForm({ ...statusForm, decisionDetails: e.target.value })}
              placeholder="Any details from the portal or notes"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUpdatePortalStatus}
              variant="contained"
              disabled={!statusForm.portalStatus || portalSubmitting}
              startIcon={portalSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
            >
              Update Status
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
